import { filter } from "lodash";
import { t } from "@/i18n-js/instance";
import { mapCurrencyAmountStrToInt } from "@circle-react/components/Paywalls/Admin/utils";
import type { PaywallCurrency } from "@circle-react/types/Paywall";
import {
  paywallPriceStatuses,
  paywallPriceTypes,
} from "@circle-react/types/PaywallPrice";
import type {
  PaywallPrice,
  PaywallPriceBuildOptionsEntry,
  PaywallPriceBuildOptionsFrequency,
  PaywallPriceFrequency,
  PaywallPriceInterval,
  PaywallPriceScenario,
  PaywallPriceScenarioCamelCase,
} from "@circle-react/types/PaywallPrice";
import { formatPaywallCurrencyAmount } from "./paywalls/currencies";

export const PRICE_SCENARIOS: Record<
  PaywallPriceScenarioCamelCase,
  PaywallPriceScenario
> = {
  value: "value",
  trialThenValue: "trial_then_value",
  upfrontThenValue: "upfront_then_value",
  trialThenUpfrontThenValue: "trial_then_upfront_then_value",
  upfrontThenTrialThenValue: "upfront_then_trial_then_value",
};

export const PRICE_TRIAL_ELIGIBILITY_CONTEXTS = {
  addingPrice: "adding_price",
  addingSubscription: "adding_subscription",
};

export const isSubscriptionPrice = (paywallPrice: any) =>
  paywallPrice?.price_type === paywallPriceTypes.subscription;
export const isInstallmentsPrice = (paywallPrice: any) =>
  paywallPrice?.price_type === paywallPriceTypes.installments;
export const isOneTimePrice = (paywallPrice: any) =>
  paywallPrice?.price_type === paywallPriceTypes.onetime;

export const isRecurringPrice = (paywallPrice: any) =>
  isSubscriptionPrice(paywallPrice) || isInstallmentsPrice(paywallPrice);

export const recurringPrices = (paywallPrices: any) =>
  filter(paywallPrices, paywallPrice => isRecurringPrice(paywallPrice));

export const isPriceActive = (paywallPrice: PaywallPrice) =>
  paywallPrice.status === paywallPriceStatuses.active;

export const isPriceEligibleForTrial = (
  paywallPrice: any,
  context: any = PRICE_TRIAL_ELIGIBILITY_CONTEXTS.addingSubscription,
) =>
  isRecurringPrice(paywallPrice) &&
  (context == PRICE_TRIAL_ELIGIBILITY_CONTEXTS.addingPrice ||
    !paywallPrice.upfront_payment_amount);

export const subscriptionPrices = (paywallPrices: any) =>
  filter(paywallPrices, paywallPrice => isSubscriptionPrice(paywallPrice));

export const pricesEligibleForTrial = (
  paywallPrices: any,
  context: any = PRICE_TRIAL_ELIGIBILITY_CONTEXTS.addingSubscription,
) =>
  filter(paywallPrices, paywallPrice =>
    isPriceEligibleForTrial(paywallPrice, context),
  );

export const findFrequencyByIntervalAndCount = (
  buildOptions: PaywallPriceBuildOptionsEntry,
  interval?: PaywallPriceInterval,
  interval_count?: number,
) =>
  Object.values(buildOptions.frequencies).find(
    (frequency: PaywallPriceBuildOptionsFrequency) =>
      frequency.interval === interval &&
      frequency.interval_count === interval_count,
  );

export const displayPaywallPrice = (paywall: any) => {
  const preferredPrice =
    paywall.stripe_prices_attributes.find(
      (price_attribute: any) => price_attribute.preferred,
    ) ?? paywall.stripe_prices_attributes[0];

  return preferredPrice
    ? preferredPrice.short_description
    : t("settings.paywalls.list.form.no_preferred_price_selected");
};

export type UpfrontPaymentVariant = "no_upfront_payment" | "upfront_payment";
export const upfrontPaymentVariants = {
  noUpfrontPayment: "no_upfront_payment",
  upfrontPayment: "upfront_payment",
} as const;

export type UpfrontPaymentDetails =
  | {
      variant: "upfront_payment";
      amountInCents: number;
      amountFormatted: string;
    }
  | {
      variant: "no_upfront_payment";
      // eslint-disable-next-line sonarjs/no-redundant-optional -- Disabled for plugin version update. Fix when possible
      amountInCents?: undefined;
      // eslint-disable-next-line sonarjs/no-redundant-optional -- Disabled for plugin version update. Fix when possible
      amountFormatted?: undefined;
    };
export const resolveUpfrontPaymentDetails = (
  currency: PaywallCurrency,
  upfrontAmount: string | number | undefined,
  options?: { shouldShowCurrency?: boolean },
): UpfrontPaymentDetails => {
  if (!upfrontAmount) {
    return { variant: upfrontPaymentVariants.noUpfrontPayment };
  }
  const amountInCents = mapCurrencyAmountStrToInt(upfrontAmount);
  if (!amountInCents) {
    return { variant: upfrontPaymentVariants.noUpfrontPayment };
  }
  const amountFormatted = formatPaywallCurrencyAmount(currency, amountInCents, {
    amountInCents: true,
    includeCurrencyCode: options?.shouldShowCurrency ?? true,
  });
  return {
    variant: upfrontPaymentVariants.upfrontPayment,
    amountInCents,
    amountFormatted,
  };
};

export const calculateInstallmentAmount = (
  upfrontPaymentDetails: UpfrontPaymentDetails,
  amount: number,
  count: number,
) => {
  if (
    upfrontPaymentDetails.variant === upfrontPaymentVariants.noUpfrontPayment
  ) {
    return Math.round(amount / count);
  }
  return Math.round((amount - upfrontPaymentDetails.amountInCents) / count);
};

export const isTrialThenValue = (priceScenario?: PaywallPriceScenario) =>
  priceScenario === PRICE_SCENARIOS.trialThenValue;

export const isUpfrontThenValue = (priceScenario?: PaywallPriceScenario) =>
  priceScenario === PRICE_SCENARIOS.upfrontThenValue;

export const isUpfrontThenTrialThenValue = (
  priceScenario?: PaywallPriceScenario,
) => priceScenario === PRICE_SCENARIOS.upfrontThenTrialThenValue;

export const isTrialThenUpfrontThenValue = (
  priceScenario?: PaywallPriceScenario,
) => priceScenario === PRICE_SCENARIOS.trialThenUpfrontThenValue;

const INTERVAL_TYPE_MAPPER: Record<
  string,
  Record<number, PaywallPriceFrequency>
> = Object.freeze({
  monthly: {
    [1]: "monthly",
    [2]: "bimonthly",
    [3]: "quarterly",
    [6]: "biannually",
  },
  yearly: {
    [1]: "yearly",
  },
  weekly: {
    [1]: "weekly",
    [2]: "biweekly",
  },
  daily: {
    [1]: "daily",
  },
});

export const getIntervalType = (price: PaywallPrice): PaywallPriceFrequency => {
  if (
    price.interval &&
    price.interval_count &&
    INTERVAL_TYPE_MAPPER[price.interval]
  ) {
    return INTERVAL_TYPE_MAPPER[price.interval][price.interval_count];
  }
  return "onetime";
};
